import React, { useCallback, useRef, useState } from 'react';
import { Animated, Platform, Pressable } from 'react-native';

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);
const useNativeDriver = Platform.OS !== 'web';
// A quick tap lifts the finger within a frame or two; the dip always runs this
// long first so every tap is visible.
const PRESS_IN_MS = 80;

/**
 * PressableScale: Pressable that dips slightly while pressed (buttons, tiles,
 * tappable cards). Same props as Pressable, plus:
 * - pressedScale (default 0.97; large cards use a gentler 0.98)
 * style may still be a function of { pressed }.
 */
const PressableScale = ({ pressedScale = 0.97, style, onPressIn, onPressOut, ...props }) => {
	const scale = useRef(new Animated.Value(1)).current;
	const pressedAt = useRef(0);
	const [isPressed, setIsPressed] = useState(false);

	const handlePressIn = useCallback((event) => {
		pressedAt.current = Date.now();
		setIsPressed(true);
		Animated.timing(scale, { toValue: pressedScale, duration: PRESS_IN_MS, useNativeDriver }).start();
		onPressIn?.(event);
	}, [onPressIn, pressedScale, scale]);

	const handlePressOut = useCallback((event) => {
		const remaining = Math.max(0, PRESS_IN_MS - (Date.now() - pressedAt.current));
		setIsPressed(false);
		Animated.sequence([
			Animated.timing(scale, { toValue: pressedScale, duration: remaining, useNativeDriver }),
			Animated.spring(scale, { toValue: 1, speed: 40, bounciness: 0, useNativeDriver }),
		]).start();
		onPressOut?.(event);
	}, [onPressOut, pressedScale, scale]);

	return (
		<AnimatedPressable
			{...props}
			onPressIn={handlePressIn}
			onPressOut={handlePressOut}
			style={[typeof style === 'function' ? style({ pressed: isPressed }) : style, { transform: [{ scale }] }]}
		/>
	);
};

export default PressableScale;
